/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';

// Component imports
import { Button, Col, Divider, Dropdown, Form, Grid, Row, Select, Space, Tabs, Tooltip, Typography } from 'antd';
import { SmileOutlined, CalendarOutlined, DashboardOutlined, MessageOutlined, ApartmentOutlined, UnorderedListOutlined, StarOutlined } from '@ant-design/icons';
import ShareAnalyticsDropdown from './ShareAnalyticsDropdown';
import ShareAnalyticsModal from './ShareAnalyticsModal';
import ExternalAnalytics from './ExternalAnalytics';
import AnalyticsSummaryTab from './AnalyticsSummaryTab';
import AnalyticsAutomatedAnswersTab from './AnalyticsAutomatedAnswersTab';
import AnalyticsLiveChatTab from './AnalyticsLiveChatTab';
import AnalyticsDecisionTreesTab from './AnalyticsDecisionTreesTab';
import AnalyticsInterviewsTab from './AnalyticsInterviewsTab';
import AnalyticsUserFeedbackTab from './AnalyticsUserFeedbackTab';
import AnalyticsMissedQuestionsTab from './AnalyticsMissedQuestionsTab';
import ObservatoryFiltersRow, { useInitialTenantFilters, useObservatoryFilters, useObservatorySearchParams } from '../UI/ObservatoryFiltersRow';
import AnalyticsCSATTab from './AnalyticsCSATTab';
import { LiveChatIcon } from '../Icons/LiveChatIcon';

// Misc imports
import handleFileDownload from '../../utils/misc/handleFileDownload';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import { useLazyExportAnalyticsReportQuery } from '../../store/analytics/analyticsApi';
import { revertBlobTypes } from '../../store/utils/revertBlobTypes';
import { ANALYTICS_SECTIONS, ANALYTICS_TYPES, ANALYTICS_TYPES_PROPERTIES } from '../../constants/main';
import { IS_HRWIZ_CONSOLE_ENABLED } from '../../constants/env';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { stringToBoolean } from '../../utils/string/toBoolean';

// Constant declarations
const { useBreakpoint } = Grid;

// Function to generate subsection keys array
const generateSubSectionKeys = (sectionKey) => {
  const section = ANALYTICS_SECTIONS[sectionKey];
  if (!section || !section.subSections) {
    return [];
  }

  return Object.keys(section.subSections).map(subKey =>
    `${section.key}_${section.subSections[subKey].key}`
  );
};

const ANALYTICS_TABS = {
  SUMMARY: {
    label: t('Summary'),
    key: 'summary',
    icon: <DashboardOutlined className="fs-xxl m-0" />,
    priority: 1,
    children: (props) => <AnalyticsSummaryTab {...props}/>,
    subSectionKeys: generateSubSectionKeys('SUMMARY')
  },
  AUTOMATED_ANSWERS: {
    label: t('Automated Answers'),
    key: 'automatedAnswers',
    icon: <MessageOutlined className="fs-xxl m-0" />,
    priority: 2,
    children: (props) => <AnalyticsAutomatedAnswersTab {...props}/>,
    subSectionKeys: generateSubSectionKeys('AUTOMATED_ANSWERS')
  },
  LIVE_CHAT: {
    label: t('LiveChat'),
    key: 'livechat',
    icon: <LiveChatIcon className="fs-xxl m-0" />,
    priority: 3,
    children: (props) => <AnalyticsLiveChatTab {...props}/>,
    subSectionKeys: generateSubSectionKeys('LIVECHAT')
  },
  DECISION_TREES: {
    label: t('Decision Trees'),
    key: 'decisionTrees',
    icon: <ApartmentOutlined rotate={270} className="fs-xxl m-0" />,
    priority: 4,
    children: (props) => <AnalyticsDecisionTreesTab {...props}/>,
    subSectionKeys: generateSubSectionKeys('DECISION_TREES')
  },
  INTERVIEWS: {
    label: t('Surveys'),
    key: 'interviews',
    icon: <CalendarOutlined className="fs-xxl m-0" />,
    priority: 5,
    children: (props) => <AnalyticsInterviewsTab {...props}/>,
    subSectionKeys: generateSubSectionKeys('INTERVIEWS')
  },
  USER_FEEDBACK: {
    label: t('User feedback'),
    key: 'userFeedback',
    icon: <SmileOutlined className="fs-xxl m-0" />,
    priority: 6,
    children: (props) => <AnalyticsUserFeedbackTab {...props}/>,
    subSectionKeys: generateSubSectionKeys('USER_FEEDBACK')
  },
  MISSED_QUESTIONS: {
    label: t('Missed Questions'),
    key: 'missedQuestions',
    icon: <UnorderedListOutlined className="fs-xxl m-0" />,
    priority: 7,
    children: (props) => <AnalyticsMissedQuestionsTab {...props}/>,
    subSectionKeys: generateSubSectionKeys('MISSED_QUESTIONS')
  },
  CSAT: {
    label: t(`Opinion Scale (CSAT)`),
    key: 'CSAT',
    icon: <StarOutlined className="fs-xxl m-0" />,
    priority: 8,
    children: (props) => <AnalyticsCSATTab {...props}/>,
    subSectionKeys: generateSubSectionKeys('CSAT')
  }
};

/**
 * This is used to filter the tabs that are shown in the organization analytics page
 * in case we want to show only a subset of the tabs
 */
export const ANALYTICS_TABS_KEYS = {
  SUMMARY: 'summary',
  AUTOMATED_ANSWERS: 'automatedAnswers',
  LIVE_CHAT: 'livechat',
  DECISION_TREES: 'decisionTrees',
  INTERVIEWS: 'interviews',
  USER_FEEDBACK: 'userFeedback',
  MISSED_QUESTIONS: 'missedQuestions',
  CSAT: 'CSAT'
};

const OrganizationAnalytics = (props) => {
  const { showTabsWithKeys, hideDashboardSelection, hideAgentsPicker } = props;
  const { orgId } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const [filtersForm] = Form.useForm();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;

  const isPublicMode = searchParams.has('token');

  const { data: bots, isLoading: isLoadingBots, isSuccess: isFetchBotsSuccessful } = useFetchBotsQuery({ orgId, urlParams: { includeBotDeployments: true } }, { skip: !orgId || isPublicMode });
  const { data: organization } = useFetchOrganizationQuery({ orgId }, { skip: isPublicMode || !orgId });
  const [exportAnalyticsReport, { isLoading: isExportingReport }] = useLazyExportAnalyticsReportQuery();

  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const observatorySearchParams = useObservatorySearchParams(orgTimezone);
  const observatoryFilters = useObservatoryFilters(orgTimezone);
  const initialTenantFilters = useInitialTenantFilters();

  // Related to sharing the public page
  const [isShareDropdownOpen, setIsShareDropdownOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [sectionsToShare, setSectionsToShare] = useState([]);
  const [includeDatePicker, setIncludeDatePicker] = useState(searchParams.has('includeDatePicker') ? stringToBoolean(searchParams.get('includeDatePicker')) : true);
  const [currentTabItem, setCurrentTabItem] = useState(searchParams.has('section') ? Object.values(ANALYTICS_TABS).find(tab => tab.key === searchParams.get('section')) : Object.values(ANALYTICS_TABS).find(tab => tab.priority === 1));
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [, updateState] = useState(); // Hook to force a component re-render

  const shouldMakeRequest = (observatorySearchParams.startDate && observatorySearchParams.endDate && organization && isFetchBotsSuccessful) || isPublicMode;

  const requestParams = shouldMakeRequest ? [
    {
      orgId,
      ...(isPublicMode && { token: searchParams.get('token') }),
      urlParams: {
        ...observatorySearchParams,
        timeZone: encodeURIComponent(orgTimezone),
        queryType: 'json'
      }
    }
  ] : [];

  const isHelviaDashboard = !filtersForm.getFieldValue('analyticsUrl');
  const pickerDates = [observatoryFilters.startDate, observatoryFilters.endDate];

  useEffect(() => {
    if (organization) {
      filtersForm.setFieldsValue({ rangePicker: pickerDates });
    }
  }, [organization, orgTimezone]);

  useEffect(() => {
    updateUrlSearchParams(currentTabItem?.key);
  }, [organization, bots, location.search, currentTabItem.key]);

  // Hide sections that are not present in the sections param - in case we're in the public analytics view
  useEffect(() => {
    if (isPublicMode) {
      const sectionsParam = searchParams.get('sections');
      if (sectionsParam) {
        const sections = sectionsParam.split(',');
        setSectionsToShare(sections || []);

        // Hide divs with IDs not found in sections
        Object.values(ANALYTICS_SECTIONS).forEach(({ key, subSections }) => {
          // If the parent ID is present in the params, skip the subSection checks
          if (sections.includes(key)) {
            return;
          }

          Object.values(subSections).forEach(subSection => {
            const id = `${key}_${subSection.key}`;
            const element = document.getElementById(`analytics-section_${id}`);
            if (element && !sections.includes(id)) {
              element.remove();
            }
          });
        });
      }
    }
  }, [isPublicMode, isLoadingAnalytics]);

  const memoizedTabItems = useMemo(() => {
    return Object.values(ANALYTICS_TABS)
      .filter(tab =>
        !isPublicMode
          ? Array.isArray(showTabsWithKeys) ? showTabsWithKeys.includes(tab.key) : true
          : sectionsToShare?.includes(tab.key) || tab.subSectionKeys?.some(subKey => sectionsToShare?.includes(subKey))
      )
      .sort((a, b) => a.priority - b.priority)
      .map(tab => ({
        label: (
          <Tooltip placement="right" title={tab?.label}>
            {tab?.icon}
          </Tooltip> || tab?.label
        ),
        key: tab.key,
        children: tab.key === currentTabItem.key ? tab?.children({
          requestParams,
          isPublicMode,
          setIsLoading: setIsLoadingAnalytics
        }) : null,
        disabled: isLoadingAnalytics
      }));
  }, [currentTabItem.key, requestParams, isLoadingAnalytics, sectionsToShare]);

  useEffect(() => {
    if (isPublicMode && memoizedTabItems.length > 0) {
      const availableTabs = memoizedTabItems.map(item => item.key);
      if (!availableTabs.includes(currentTabItem.key)) {
        const newCurrentTab = Object.values(ANALYTICS_TABS).find(tab => availableTabs.includes(tab.key));
        setCurrentTabItem(newCurrentTab);
      }
    }
  }, [memoizedTabItems, currentTabItem.key, isPublicMode]);

  const onTabChange = (key) => {
    setCurrentTabItem(Object.values(ANALYTICS_TABS).find(tab => tab.key === key));
  };

  const exportAnalyticsPdf = async () => {
    if (observatorySearchParams.startDate && observatorySearchParams.endDate && orgTimezone) {
      const result = await exportAnalyticsReport({ orgId, urlParams: {
        ...observatorySearchParams,
        timeZone: encodeURIComponent(orgTimezone),
        queryType: 'json'
      } }).unwrap();

      const file = revertBlobTypes(result, 'application/pdf');
      if (file) {
        const fileName = file.headers['content-disposition'] ?? `report.pdf`;
        handleFileDownload(file.blob, fileName);
      }
    }
  };

  const updateUrlSearchParams = (currentAnalyticsTabKey) => {
    if (!searchParams.has('tab')) {
      searchParams.set('tab', 'analytics');
    }

    if (currentAnalyticsTabKey) {
      searchParams.set('section', currentAnalyticsTabKey);
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });
  };

  const renderFiltersSection = () => {
    return (
      <Form
        layout="horizontal"
        name="filtersForm"
        className="align-items-center"
        initialValues={{
          tenantsFilter: initialTenantFilters,
          rangePicker: pickerDates,
          analyticsUrl: null
        }}
        form={filtersForm}
        onValuesChange={(_, allFields) => {
          updateState(allFields);
        }}
      >
        <ObservatoryFiltersRow
          form={filtersForm}
          rangePickerProps={{
            disabled: isLoadingAnalytics,
            timezone: orgTimezone,
            hidden: !isHelviaDashboard || (isPublicMode && !includeDatePicker)
          }}
          agentsPickerProps={{
            agentsList: bots?.items || [],
            disabled: isLoadingAnalytics || isLoadingBots,
            hidden: !isHelviaDashboard || isPublicMode || hideAgentsPicker,
            loading: isLoadingBots
          }}
        />

        <Row gutter={[16, 16]} className="mb-4 hide-empty">
          {
            // Dashboard selection column
            !isPublicMode && !hideDashboardSelection ? (
              <Col flex="none">
                <Form.Item
                  name="analyticsUrl"
                  noStyle
                >
                  <Select
                    size="large"
                    dropdownMatchSelectWidth={false}
                  >
                    <Select.Option key={ANALYTICS_TYPES.HELVIA} value={null}>
                      <Space size={5}>
                        {ANALYTICS_TYPES_PROPERTIES.find(analytics => analytics.type === ANALYTICS_TYPES.HELVIA).icon}
                        {ANALYTICS_TYPES_PROPERTIES.find(analytics => analytics.type === ANALYTICS_TYPES.HELVIA).displayName}
                      </Space>
                    </Select.Option>

                    {
                      organization?.externalAnalytics?.map(analytics => (
                        <Select.Option key={analytics.displayName} value={analytics.url}>
                          <Space size={5}>
                            {ANALYTICS_TYPES_PROPERTIES.find(property => property.type === analytics.type)?.icon}
                            {analytics.displayName}
                          </Space>
                        </Select.Option>
                      ))
                    }
                  </Select>
                </Form.Item>
              </Col>
            ) : null
          }

          {
            // Share and export buttons column
            isHelviaDashboard && !isPublicMode ? (
              <Col flex="auto" className="text-right">
                <Space className="flex-wrap">
                  {
                    !IS_HRWIZ_CONSOLE_ENABLED ? (
                      <Dropdown
                        disabled={isLoadingAnalytics}
                        trigger="click"
                        onOpenChange={setIsShareDropdownOpen}
                        open={isShareDropdownOpen}
                        overlayClassName="w-100"
                        overlayStyle={{ maxWidth: 300 }}
                        overlay={
                          <ShareAnalyticsDropdown
                            onShare={(sections, hasDatePicker) => {
                              setIsShareDropdownOpen(false);
                              setSectionsToShare(sections);
                              setIncludeDatePicker(hasDatePicker);
                              setIsShareModalOpen(true);
                            }}
                          />
                        }
                        placement="bottomRight"
                      >
                        <Button
                          size="large"
                        >
                          {t('Share')}
                        </Button>
                      </Dropdown>
                    ) : null
                  }

                  <Button
                    type="primary"
                    size="large"
                    onClick={exportAnalyticsPdf}
                    disabled={isLoadingAnalytics}
                    loading={isExportingReport}
                  >
                    {t('Export as PDF')}
                  </Button>
                </Space>
              </Col>
            ) : null
          }
        </Row>
      </Form>
    );
  };

  const renderAnalyticsContainer = () => {
    if (!isHelviaDashboard) {
      return <ExternalAnalytics url={filtersForm.getFieldValue('analyticsUrl')}/>;
    } else {
      return (
        <Fragment>
          <Row>
            <Col xs={24}>
              <Divider orientation="left">
                <Typography.Text>
                  {currentTabItem?.icon}
                </Typography.Text>
                <Typography.Text className="fs-xl ml-2">
                  {t(currentTabItem?.key, { context: 'analytics section' })}
                </Typography.Text>
              </Divider>
            </Col>

            <Col xs={24}>
              <Tabs
                type="card"
                className="analytics-tab"
                activeKey={currentTabItem.key}
                defaultActiveKey={Object.values(ANALYTICS_TABS).find(tab => tab.priority === 1)}
                onChange={onTabChange}
                tabPosition={`${isMobileOrTabletSize ? 'top' : 'left'}`}
                items={memoizedTabItems}
                tabBarGutter={8}
              />
            </Col>
          </Row>
        </Fragment>
      );
    }
  };

  return (
    <Fragment>
      {renderFiltersSection()}

      {renderAnalyticsContainer()}

      <ShareAnalyticsModal
        open={isShareModalOpen}
        setOpen={setIsShareModalOpen}
        sectionsToShare={sectionsToShare}
        includeDatePicker={includeDatePicker}
      />
    </Fragment>
  );
};

OrganizationAnalytics.propTypes = {
  showTabsWithKeys: PropTypes.arrayOf(PropTypes.string),
  hideDashboardSelection: PropTypes.bool,
  hideAgentsPicker: PropTypes.bool
};

export default OrganizationAnalytics;
